import React, { useCallback, useContext, useEffect, useState } from 'react'
import Decimal from 'decimal.js'
import { ec } from 'elliptic'
import { keccak256 } from 'js-sha3'
import { map, distinctUntilChanged } from 'rxjs'
import { providers, keyStores, KeyPair } from 'near-api-js'
import { setupWalletSelector } from '@near-wallet-selector/core'
import { setupModal } from '@near-wallet-selector/modal-ui-js'
import { setupHereWallet } from '@near-wallet-selector/here-wallet'
import { setupMeteorWallet } from '@near-wallet-selector/meteor-wallet'
import { setupMyNearWallet } from '@near-wallet-selector/my-near-wallet'
import { setupNightly } from '@near-wallet-selector/nightly'

import contractHelper from '@/api/contractHelper'
import { getKeychainString } from '@/utils/authentication'
import {
  DEFAULT_NETWORK,
  CONTRACT_MAP,
  ASSET_MANAGER_CONTRACT_METHOD_NAMES,
  LOCAL_STORAGE_KEYCHAIN,
  WALLET_KEY_STORE_PREFIX_MAP,
  MIN_NEAR_WALLET_BALANCE
} from '@/constants/config'

// import { setupSender } from '@near-wallet-selector/sender'
// import { setupMathWallet } from '@near-wallet-selector/math-wallet'
// import { setupWalletConnect } from '@near-wallet-selector/wallet-connect'
// import { setupLedger } from '@near-wallet-selector/ledger'

// const queryString = window.location.search
// const urlParams = new URLSearchParams(queryString)
// const transactionHashes = urlParams.get('transactionHashes')
// const accountId = accounts.find(account => account.active)?.accountId || null

// console.log('transactionHashes', transactionHashes)

// find
// user_account_exists -> storage_deposit -> user_announce_key -> user_request_set_trading_key
// user_account_exists -> is_orderly_key_announced? -> user_announce_key -> user_request_set_trading_key
// user_account_exists -> is_orderly_key_announced? -> get_user_trading_key -> user_request_set_trading_key
// user_account_exists -> is_orderly_key_announced? -> get_user_trading_key

function getActiveAccountId(accountList) {
  return accountList.find(account => account.active)?.accountId || null
}

async function getKeyPair(network, walletId, accountId) {
  if (walletId === 'here-wallet') {
    const keyStore = JSON.parse(localStorage.getItem('herewallet:keystore'))
    const { accounts, activeAccount } = keyStore[network] || {}
    const key = accounts[activeAccount]
    return KeyPair.fromString(key)
  } else {
    const keyStorePrifix = WALLET_KEY_STORE_PREFIX_MAP[walletId]
    const keyStore = new keyStores.BrowserLocalStorageKeyStore(
      window.localStorage,
      keyStorePrifix
    )
    return await keyStore.getKey(network, accountId)
  }
}

const secp256k1 = new ec('secp256k1')
const WalletSelectorContext = React.createContext(null)

export const WalletSelectorContextProvider = ({ children }) => {
  const [network, setNetwork] = useState(DEFAULT_NETWORK)
  const [selector, setSelector] = useState(null)
  const [modal, setModal] = useState(null)
  // const [accounts, setAccounts] = useState([])
  const [accountId, setAccountId] = useState(null)
  const [walletId, setWalletId] = useState('')
  const [walletReady, setWalletReady] = useState(false)
  const [isInsufficientBalance, setIsInsufficientBalance] = useState(false)
  const [initialError, setInitialError] = useState('')

  const init = useCallback(async () => {
    const contractId = CONTRACT_MAP[network]
    const _selector = await setupWalletSelector({
      network,
      languageCode: 'en',
      // debug: true,
      optimizeWalletOrder: true,
      modules: [
        setupHereWallet(),
        setupMeteorWallet(),
        setupMyNearWallet()
        // setupNightly()
        // setupWalletConnect({
        //   projectId: '2f82a30e64e68332d64b4b06ce4ad179',
        //   metadata: {
        //     name: 'BTSE DEX',
        //     description:
        //       'BTSE Defi cryptocurrency exchange - Your best choice for trading Bitcoin with lowest fees, deep liquidity, the most trusted and secure exchange in the world.',
        //     url: 'https://dex.btse.co',
        //     icons: ['https://www.btse.com/static/img/manifest-icon-192.png']
        //   },
        //   chainId: `near:${network}`
        // }),
        // setupLedger()
      ]
    })
    const _modal = setupModal(_selector, {
      contractId,
      methodNames: ASSET_MANAGER_CONTRACT_METHOD_NAMES,
      theme: 'dark'
    })
    const _provider = new providers.JsonRpcProvider({
      url: _selector.options.network.nodeUrl
    })
    const state = _selector.store.getState()
    const accountId = state.selectedWalletId
      ? getActiveAccountId(state.accounts)
      : null

    contractHelper.setConfig(_provider, contractId)

    state.selectedWalletId &&
      contractHelper.setWalletInfo(await _selector.wallet(), accountId)

    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    urlParams.get('transactionHashes') &&
      window.history.replaceState({}, document.title, window.location.pathname)

    setSelector(_selector)
    setModal(_modal)
    accountId && setAccountId(accountId)
  }, [network])

  const signOut = useCallback(async () => {
    const wallet = await selector.wallet()

    wallet
      .signOut()
      .then(() => {
        localStorage.removeItem(LOCAL_STORAGE_KEYCHAIN)
        setWalletReady(false)
        init()
      })
      .catch()
  }, [init, selector])

  useEffect(() => {
    init().catch(err => {
      // console.error(err)
      alert('Failed to initialise wallet selector')
    })
  }, [init])

  useEffect(() => {
    if (!selector || accountId === null) {
      return
    }

    const state = selector.store.getState()
    let initialProcessStatus = ''
    let orderlyTradingKey = null
    setIsInsufficientBalance(false)

    selector.wallet().then(wallet => {
      contractHelper.setWalletInfo(wallet, accountId)
      getKeyPair(network, state.selectedWalletId, accountId)
        .then(async keyPair => {
          const keychainString = getKeychainString()
          const orderlyKey = keyPair ? keyPair.getPublicKey().toString() : ''
          const [isAccountExists, nearBalance] = await Promise.all([
            contractHelper.checkUserAccountExists(),
            contractHelper.getWalletNearBalance()
          ]).catch(() => [false, 0])
          const [isOrderlyKeyAnnounced, isTradingKeySet] =
            isAccountExists && orderlyKey
              ? await Promise.all([
                  contractHelper.checkIsOrderlyKeyAnnounced(orderlyKey),
                  contractHelper.checkIsTradingKeySet(orderlyKey)
                ])
              : [false, false]

          initialProcessStatus = `${isAccountExists ? '👤' : ''}${
            isOrderlyKeyAnnounced ? '🔑' : ''
          }${isTradingKeySet ? '🔐' : ''}`
          console.log(`👤 Account ${isAccountExists ? '✅' : '❎'}`)
          console.log(`🔑 Orderly Key ${isOrderlyKeyAnnounced ? '✅' : '❎'}`)
          console.log(`🔐 Trading Key ${isTradingKeySet ? '✅' : '❎'}`)

          if (
            !keychainString &&
            isAccountExists &&
            isOrderlyKeyAnnounced &&
            isTradingKeySet
          ) {
            console.log('🤯 keychain not found...')
            return signOut()
          }

          if (
            (!isAccountExists || !isOrderlyKeyAnnounced || !isTradingKeySet) &&
            Decimal(nearBalance).lt(MIN_NEAR_WALLET_BALANCE)
          ) {
            console.log('🤯 insufficient balance...')
            signOut()
            return setIsInsufficientBalance(true)
          }

          if (!isOrderlyKeyAnnounced) {
            await contractHelper.initialStorageBalance(isAccountExists)
            await contractHelper.announceOrderlyKey()
          }

          if (!isTradingKeySet) {
            const key = secp256k1.genKeyPair()
            const publicKey = key.getPublic().encode('hex')
            orderlyTradingKey = window.btoa(
              keccak256(Buffer.from(publicKey.replace('04', '')))
            )
            await contractHelper.setOrderlyTradingKey(orderlyTradingKey)

            localStorage.setItem(
              LOCAL_STORAGE_KEYCHAIN,
              window.btoa(
                JSON.stringify({
                  accountId,
                  orderlyKey: keyPair.toString(),
                  orderlyTradingKey: key.getPrivate().toString('hex')
                })
              )
            )
          }
          setTimeout(() => setWalletReady(true), 1000)
        })
        .catch(error => {
          console.error(error)
          setInitialError({
            initialProcessStatus,
            error
          })
        })
    })
  }, [network, selector, accountId, setWalletReady, signOut])

  useEffect(() => {
    if (!selector) {
      return
    }

    const walletIdSubscription = selector.store.observable
      .pipe(
        map(state => state.selectedWalletId),
        distinctUntilChanged()
      )
      .subscribe(nextSelectedWalletId => {
        // console.log('Wallet Id Update', nextSelectedWalletId)

        setWalletId(nextSelectedWalletId)
      })

    const accountSubscription = selector.store.observable
      .pipe(
        map(state => state.accounts),
        distinctUntilChanged()
      )
      .subscribe(nextAccounts => {
        // console.log('Accounts Update', nextAccounts)
        const accountId = getActiveAccountId(nextAccounts) || null
        setAccountId(accountId)
        // setAccounts(nextAccounts)
      })

    return () => {
      walletIdSubscription.unsubscribe()
      accountSubscription.unsubscribe()
    }
  }, [selector])

  if (!selector || !modal) {
    return null
  }

  return (
    <WalletSelectorContext.Provider
      value={{
        selector,
        modal,
        walletId,
        accountId,
        walletReady,
        isInsufficientBalance,
        initialError,
        setWalletNetwork: setNetwork,
        signOut
      }}
    >
      {children}
    </WalletSelectorContext.Provider>
  )
}

export function useWalletSelector() {
  const context = useContext(WalletSelectorContext)

  if (!context) {
    throw new Error(
      'useWalletSelector must be used within a WalletSelectorContextProvider'
    )
  }

  return context
}
